"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";

const SESSION_MS = 60 * 60 * 1000;
const SESSION_COOKIE_MAX_AGE = 36000;
const REDIRECT_BUFFER_MS = 1000;
const ACTIVITY_THROTTLE_MS = 5000;
const COOKIE_VALID_TO = "verification_valid_to";
const COOKIE_SESSION_EXPIRES_AT = "session_expires_at";

function readCookie(name: string): string | null {
    const match = document.cookie
        .split("; ")
        .find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number) {
    const secure = location.protocol === "https:" ? "; secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; samesite=lax${secure}`;
}

export default function ExpiryGuard({ locale }: Readonly<{ locale: string }>) {
    const pathname = usePathname();
    const router = useRouter();

    const onErrorPage = pathname?.includes("/error") ?? false;
    const onWelcome = pathname?.includes("/welcome") ?? false;
    const onStep = pathname?.includes("/step") ?? false;

    useEffect(() => {
        if (onErrorPage || onWelcome) return;
        const raw = readCookie(COOKIE_VALID_TO);
        if (!raw) return;
        const ms = Number(raw);
        if (!ms) return;

        const goExpired = () => router.replace(`/${locale}/error?reason=link`);
        const delay = ms - Date.now();
        if (delay <= 0) {
            goExpired();
            return;
        }
        if (delay > 2 ** 31 - 1) return;
        const id = setTimeout(goExpired, delay);
        return () => clearTimeout(id);
    }, [pathname, onErrorPage, onWelcome, locale, router]);

    useEffect(() => {
        if (onErrorPage || !onStep) return;

        let timer: ReturnType<typeof setTimeout>;
        let last = 0;

        const expire = () => router.replace(`/${locale}/error?reason=session`);

        const slide = () => {
            const expiresAt = Date.now() + SESSION_MS;
            writeCookie(
                COOKIE_SESSION_EXPIRES_AT,
                String(expiresAt),
                SESSION_COOKIE_MAX_AGE,
            );
            clearTimeout(timer);
            timer = setTimeout(expire, SESSION_MS + REDIRECT_BUFFER_MS);
        };

        const onActivity = () => {
            const now = Date.now();
            if (now - last > ACTIVITY_THROTTLE_MS) {
                last = now;
                slide();
            }
        };

        const events = [
            "mousemove",
            "mousedown",
            "keydown",
            "scroll",
            "touchstart",
        ];

        events.forEach((e) =>
            window.addEventListener(e, onActivity, { passive: true }),
        );

        slide();

        return () => {
            clearTimeout(timer);
            events.forEach((e) => window.removeEventListener(e, onActivity));
        };
    }, [onErrorPage, onStep, locale, router]);

    return null;
}
